import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Info, RefreshCw } from 'lucide-react';
import Block from './Block';
import Disclosure from './Disclosure';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Select } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Tooltip } from '../../components/ui/tooltip';
import { TSForecastChart } from './TimeSeriesForecastBlock';
import { toneOf } from './CausalEffectBlock';
import { mapeTone, mapeLabel, MAPE_TOOLTIP, maseTone, maseLabel, maeTooltip, formatMetricValue } from './metricTone';

const fmt = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
};

const prettyName = (s) => String(s || '').replace(/_/g, ' ');

// SKUs/ISINs/sensor ids are rarely human-recognizable on their own — when a
// display name is available (id_name_column upstream), lead with it and keep
// the id as a parenthetical for anyone who needs the exact identifier.
const seriesLabel = (itemName, itemId) => (
  itemName && itemName !== itemId ? `${prettyName(itemName)} (${itemId})` : prettyName(itemId)
);

// Pandas frequency codes -> human-readable labels.
const FREQ_LABELS = {
  H: 'hourly', T: 'minutely', MIN: 'minutely', D: 'daily', B: 'business-daily',
  W: 'weekly', M: 'monthly', MS: 'monthly', Q: 'quarterly', QS: 'quarterly',
  A: 'yearly', Y: 'yearly', AS: 'yearly', YS: 'yearly',
};
function freqLabel(freq) {
  if (!freq) return '';
  const key = String(freq).toUpperCase().split('-')[0];
  return FREQ_LABELS[key] || String(freq);
}

// Backtest accuracy badge. The metric is chosen server-side per series — see
// chronos_worker.py's near-zero-actuals switch. MAPE tiers off its own percent
// thresholds; MAE has no universal threshold, so it tiers off MASE (its ratio
// to a naive "nothing changes" forecast) while still displaying the MAE itself,
// which is the number that means something in the user's units.
const TONE_VARIANT = { green: 'success', warn: 'warning', error: 'destructive' };

function MetricBadge({ metricName, metricValue, mape, mase }) {
  const name = metricName || (mape != null ? 'MAPE' : null);
  const value = metricValue != null ? metricValue : mape;
  if (name == null || value == null || !isFinite(value)) return null;
  const isMape = name === 'MAPE';
  const tone = isMape ? mapeTone(value) : maseTone(mase);
  const word = isMape ? mapeLabel(value) : maseLabel(mase);
  const label = word ? ` · ${word}` : '';
  const tooltip = isMape ? MAPE_TOOLTIP : maeTooltip(mase);
  const displayValue = isMape ? value.toFixed(1) : formatMetricValue(value);
  return (
    <Tooltip content={tooltip}>
      <Badge variant={TONE_VARIANT[tone] || 'secondary'} tabIndex={0} aria-label={tooltip} style={{ cursor: 'help' }}>
        Backtest {name} {displayValue}{isMape ? '%' : ''}{label}
        <Info aria-hidden />
      </Badge>
    </Tooltip>
  );
}

// How many category chips fit the space a sparkline would occupy.
const MAX_COV_CHIPS = 4;

// Categorical covariates have no line to draw — show their most common values
// instead, which is the only thing a tile can usefully say about them.
function CategoryChips({ categories = [], nCategories = 0 }) {
  const shown = categories.slice(0, MAX_COV_CHIPS);
  const rest = Math.max((nCategories || categories.length) - shown.length, 0);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minHeight: 72, alignContent: 'flex-start' }}>
      {shown.map((c) => (
        <Badge key={c.value} variant="secondary" style={{ maxWidth: '100%' }}>
          <span className="q-truncate">{c.value}</span>
        </Badge>
      ))}
      {rest > 0 && <Badge variant="outline">+{rest}</Badge>}
    </div>
  );
}

function CovariateCard({ series }) {
  const isCategorical = series.kind === 'categorical' || (series.categories || []).length > 0;
  return (
    <Card style={{ overflow: 'visible', boxShadow: 'none' }}>
      <CardContent style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span className="q-truncate" style={{ fontSize: 15, fontWeight: 500, color: 'var(--foreground)', textTransform: 'capitalize' }}>
            {prettyName(series.name)}
          </span>
          <span className="q-muted" style={{ fontSize: 13.5 }}>
            {series.role === 'future_covariate' ? 'Known in future' : 'History only'}
          </span>
        </div>
        {isCategorical ? (
          <CategoryChips categories={series.categories || []} nCategories={series.n_categories} />
        ) : (
          <TSForecastChart
            historical={series.historical || []}
            forecast={series.forecast || []}
            width={200}
            height={72}
            showAxis={false}
          />
        )}
      </CardContent>
    </Card>
  );
}

// Flatten groups into one page per series (item_id). Each page carries that
// series' target(s) plus only its own covariates, so a long-format dataset with
// one shared target column (e.g. units_sold across 10 SKUs) becomes 10 pages
// instead of one wall of stacked charts.
function buildPages(groups) {
  const pages = [];
  for (const g of groups) {
    const order = [];
    const byItem = new Map();
    for (const s of (g.series || [])) {
      const iid = s.item_id == null ? 'series' : String(s.item_id);
      if (!byItem.has(iid)) { byItem.set(iid, []); order.push(iid); }
      byItem.get(iid).push(s);
    }
    for (const iid of order) {
      const ss = byItem.get(iid);
      const named = ss.find((s) => s.item_name);
      pages.push({
        groupId: g.group_id || '',
        itemId: iid,
        itemName: named ? named.item_name : '',
        targets: ss.filter((s) => s.role === 'target'),
        covariates: ss.filter((s) => s.role !== 'target'),
      });
    }
  }
  // Page order follows the spec's group order, which is arbitrary to a reader
  // paging through SKUs — sort by item id instead, numerically aware so SKU2
  // comes before SKU10.
  pages.sort((a, b) => a.itemId.localeCompare(b.itemId, undefined, { numeric: true, sensitivity: 'base' }));
  return pages;
}

const MultiSeriesForecastBlock = ({ blockData }) => {
  const {
    groups = [],
    horizon = 0,
    freq = '',
    forecast_source: forecastSource = '',
    forecast_run_at: forecastRunAt = '',
  } = blockData || {};

  const [pageIdx, setPageIdx] = useState(0);
  if (!groups.length) return null;

  const pages = buildPages(groups);
  if (!pages.length) return null;

  const safeIdx = Math.min(Math.max(pageIdx, 0), pages.length - 1);
  const page = pages[safeIdx];
  const targets = page.targets;
  const covariates = page.covariates;
  const multiGroup = new Set(pages.map((p) => p.groupId)).size > 1;
  const freqWord = freqLabel(freq);

  // The message list serves this block re-hydrated from the latest
  // timeseries_forecast artifact, so a scheduled pipeline run replaces what an
  // older chat message shows — the dated pill tells the user which forecast
  // run they are looking at. Chat-triggered runs (and legacy payloads without
  // provenance) show no pill.
  const pipelineDate = forecastSource === 'pipeline' && forecastRunAt
    ? new Date(forecastRunAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  const description = [
    `${horizon} ${freqWord ? `${freqWord} ` : ''}step${horizon !== 1 ? 's' : ''} ahead`,
    pages.length > 1 && `${pages.length} series`,
  ].filter(Boolean).join(' · ');

  const seriesOptions = pages.map((p, i) => ({
    value: i,
    label: `${seriesLabel(p.itemName, p.itemId)}${multiGroup && p.groupId ? ` · ${prettyName(p.groupId)}` : ''}`,
  }));

  return (
    <Block
      title="Time-Series Forecast"
      icon="trending-up"
      description={description}
      badge={pipelineDate ? (
        <Badge variant="secondary">
          <RefreshCw aria-hidden />
          Updated {pipelineDate}
        </Badge>
      ) : null}
    >
      {pages.length > 1 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Select
              size="sm"
              value={safeIdx}
              onValueChange={setPageIdx}
              options={seriesOptions}
              label="Series"
              ariaLabel="Choose a series"
              minMenuWidth={220}
              className="q-series-select"
            />
            <span className="q-muted q-num" style={{ fontSize: 14.5 }}>
              {safeIdx + 1} of {pages.length}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Previous series"
                disabled={safeIdx === 0}
                onClick={() => setPageIdx(safeIdx - 1)}
              >
                <ChevronLeft aria-hidden />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Next series"
                disabled={safeIdx === pages.length - 1}
                onClick={() => setPageIdx(safeIdx + 1)}
              >
                <ChevronRight aria-hidden />
              </Button>
            </div>
          </div>
          <Separator style={{ margin: '18px 0 22px' }} />
        </>
      )}

      {/* Target series — large, stacked. */}
      {targets.map((t, i) => {
        const hist = t.historical || [];
        const fc = t.forecast || [];
        const lastActual = hist[hist.length - 1];
        const last = fc[fc.length - 1];
        const change = lastActual && last && lastActual.value
          ? ((last.value - lastActual.value) / Math.abs(lastActual.value)) * 100
          : null;
        return (
          <div key={`${t.name}-${t.item_id}-${i}`} style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 16.5, fontWeight: 500, color: 'var(--foreground)', textTransform: 'capitalize' }}>
                {prettyName(t.name)}
                {/* Show the series id inline only when there's no pager above to
                    name it (single page) and the id is meaningful. */}
                {pages.length === 1 && t.item_id && t.item_id !== 'series'
                  ? <span className="q-muted" style={{ fontWeight: 500 }}>
                      {' '}· {seriesLabel(page.itemName, t.item_id)}
                    </span>
                  : null}
              </div>
              <div style={{ flex: 1 }} />
              <MetricBadge
                metricName={t.metric_name}
                metricValue={t.metric_value}
                mape={t.mape}
                mase={t.mase}
              />
            </div>
            <TSForecastChart historical={hist} forecast={fc} width={700} height={320} />
            {last && (
              <div className="q-stats" style={{ marginTop: 20 }}>
                {lastActual && (
                  <div className="q-stat">
                    <span className="q-stat__label">Last actual</span>
                    <span className="q-stat__value">{fmt(lastActual.value)}</span>
                    <span className="q-stat__hint">{String(lastActual.date || '').slice(0, 10)}</span>
                  </div>
                )}
                <div className="q-stat">
                  <span className="q-stat__label">Forecast at horizon end</span>
                  <span className="q-stat__value">
                    {fmt(last.value)}
                    {change != null && isFinite(change) && (
                      <Badge variant={toneOf(change)} className="ui-badge--mono">
                        {change > 0 ? '+' : ''}{change.toFixed(1)}%
                      </Badge>
                    )}
                  </span>
                  <span className="q-stat__hint">{String(last.date || '').slice(0, 10)}</span>
                </div>
                {last.ci_low != null && last.ci_high != null && (
                  <div className="q-stat">
                    <span className="q-stat__label">90% interval</span>
                    <span className="q-stat__value">{fmt(last.ci_low)}–{fmt(last.ci_high)}</span>
                    <span className="q-stat__hint">at horizon end</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {covariates.length > 0 && (
        <Disclosure label="Covariates" meta={covariates.length} defaultOpen>
          <div style={{
            display: 'grid',
            // Column count adapts to both the number of covariates and the
            // available width, so a handful never stretch into oversized cards.
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 12,
            paddingTop: 6,
          }}>
            {covariates.map((c, i) => (
              <CovariateCard key={`${c.name}-${c.item_id}-${i}`} series={c} />
            ))}
          </div>
        </Disclosure>
      )}
    </Block>
  );
};

export default MultiSeriesForecastBlock;
